import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { BdcDispatchNote } from "../../../types/bdc.type";
import { pcs } from "../format";

const PAPER_FONT = "Sarabun, Prompt, sans-serif";

/** `2026-10-15` → `15/10/2569` as the paper heads it. */
const paperDate = (value: string): string => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${Number(match[1]) + 543}` : value;
};

const blankOr = (value: string | number | null, dots: string) =>
  value === null || value === "" ? dots : typeof value === "number" ? pcs(value) : value;

/**
 * ตัวอย่างใบที่พิมพ์ — one DC's block of the A4 (2 DCs per portrait page, like the Excel).
 * ขาด / เหลือ and the foot are blank for the driver to write, or carry what was recorded.
 */
export default function PaperPreview({ note, title }: Readonly<{ note: BdcDispatchNote; title: string }>) {
  const footer: Array<[string, string]> = [
    ["จำนวนถาดขึ้น", String(blankOr(note.trays_out, "............"))],
    ["ผู้จ่าย", String(blankOr(note.issuer_name, "..........................."))],
    ["จำนวนถาดกลับ", String(blankOr(note.trays_back, "..........."))],
    ["เจ้าหน้าที่บันทึก", String(blankOr(note.recorder_name, ".............."))],
    ["จำนวนขนม CN", `${blankOr(note.cn_pcs, "........")} ชิ้น`],
    ["เวลากลับ", `${blankOr(note.return_time, "..............")} น.`],
    ["เลขไมล์ออก", String(blankOr(note.mileage_out, "..............."))],
    ["เลขไมล์เข้า", String(blankOr(note.mileage_in, "..............."))],
  ];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">
        ตัวอย่างใบที่พิมพ์ · A4 แนวตั้ง 2 DC ต่อหน้า (เหมือน Excel เดิม)
      </Typography>
      <Box
        data-testid="bdc-dispatch-paper"
        sx={{
          bgcolor: "#FFFFFF",
          border: "1px solid #C9CBD2",
          boxShadow: "0 1px 3px rgba(0,0,0,.08)",
          p: 2.25,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          fontFamily: PAPER_FONT,
          color: "#111",
          "& table.paper": { borderCollapse: "collapse", width: "100%" },
          "& .paper th, & .paper td": {
            border: "1px solid #9A9EA8",
            fontFamily: PAPER_FONT,
            fontSize: 12,
            padding: "3px 6px",
            whiteSpace: "nowrap",
          },
          "& .paper th": { fontWeight: 600 },
          "& .paper .n": { textAlign: "right", fontVariantNumeric: "tabular-nums" },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
          <b style={{ fontSize: 15 }}>{title}</b>
          <span style={{ fontSize: 12 }}>
            {note.weekday} {paperDate(note.work_date)}
          </span>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 13, flexWrap: "wrap" }}>
          <span>
            คลัง{note.dc_name} ({note.dc_code})
          </span>
          <span>ชื่อพนักงานจัดส่ง {note.driver_name ?? ".............................."}</span>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <table className="paper">
            <thead>
              <tr>
                <th>รายการ</th>
                <th className="n">เบิก</th>
                <th className="n">ถาด</th>
                <th className="n">ชิ้น</th>
                <th style={{ width: 54 }}>ขาด</th>
                <th style={{ width: 54 }}>เหลือ</th>
              </tr>
            </thead>
            <tbody>
              {note.lines.map((line) => (
                <tr key={line.barcode}>
                  <td>{line.name}</td>
                  <td className="n">{pcs(line.issued_qty)}</td>
                  <td className="n">{line.trays}</td>
                  <td className="n">{line.pieces}</td>
                  <td className="n">{line.short_qty === null ? "" : pcs(line.short_qty)}</td>
                  <td className="n">{line.leftover_qty === null ? "" : pcs(line.leftover_qty)}</td>
                </tr>
              ))}
              <tr>
                <td>
                  <b>ยอดรวม</b>
                </td>
                <td className="n">
                  <b>{pcs(note.totals.issued_qty)}</b>
                </td>
                <td className="n">
                  <b>{pcs(note.totals.trays)}</b>
                </td>
                <td className="n">
                  <b>{pcs(note.totals.pieces)}</b>
                </td>
                <td className="n">{note.totals.short_qty === null ? "" : pcs(note.totals.short_qty)}</td>
                <td className="n">{note.totals.leftover_qty === null ? "" : pcs(note.totals.leftover_qty)}</td>
              </tr>
            </tbody>
          </table>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "4px 16px",
            fontSize: 12,
            pt: 0.75,
          }}
        >
          {footer.map(([label, value]) => (
            <span key={label}>
              {label} {value}
            </span>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
